<template>
  <div class="row">
    <div class="col-md-12">
      <div class="card">
        <div class="card-header">
          <h4 class="card-title">Diffusions</h4>
          <p class="card-category">Track job posting diffusion across external platforms</p>
        </div>
        <div class="card-body">
          <!-- Filters -->
          <div class="row mb-3">
            <div class="col-md-3">
              <b-form-input
                v-model="filters.search"
                placeholder="Search by job title..."
                debounce="300"
                @update="fetchDiffusions"
              ></b-form-input>
            </div>
            <div class="col-md-2">
              <b-form-select
                v-model="filters.status"
                :options="statusOptions"
                @change="fetchDiffusions"
              ></b-form-select>
            </div>
            <div class="col-md-3">
              <b-form-select
                v-model="filters.platform"
                :options="platformOptions"
                @change="fetchDiffusions"
              ></b-form-select>
            </div>
            <div class="col-md-2">
              <b-form-select
                v-model="filters.billed"
                :options="billedOptions"
                @change="fetchDiffusions"
              ></b-form-select>
            </div>
            <div class="col-md-2 d-flex align-items-center">
              <b-form-checkbox
                v-model="filters.ats_unpublished"
                @change="$nextTick(() => fetchDiffusions())"
                switch
              >
                <span class="small">ATS Unpublished</span>
              </b-form-checkbox>
            </div>
            <div class="col-md-2 d-flex align-items-center">
              <b-form-checkbox
                v-model="filters.removed_from_feed"
                @change="$nextTick(() => fetchDiffusions())"
                switch
              >
                <span class="small">Removed from feed</span>
              </b-form-checkbox>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <BTable
              :items="diffusions"
              :fields="fields"
              :busy="isBusy"
              hover
              show-empty
              empty-text="No diffusions found"
            >
              <template #table-busy>
                <div class="text-center my-4">
                  <b-spinner class="align-middle"></b-spinner>
                  <span class="ml-2">Loading...</span>
                </div>
              </template>

              <template #cell(title)="{ item }">
                <router-link
                  v-if="item.offer"
                  :to="{ name: 'offer', params: { offerId: item.offer.id } }"
                >
                  {{ getOfferTitle(item.offer) }}
                </router-link>
                <b-badge v-if="item.removed_from_feed_at" variant="warning" class="ml-1">Removed from feed</b-badge>
                <b-badge v-if="item.ats_unpublished" variant="danger" class="ml-1">ATS Unpublished</b-badge>
                <span v-else-if="!item.offer" class="text-muted">-</span>
              </template>

              <template #cell(company)="{ item }">
                <router-link
                  v-if="item.offer && item.offer.company"
                  :to="{ name: 'company', params: { companyId: item.offer.company.id } }"
                >
                  {{ item.offer.company.name_fr }}
                </router-link>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(platform_name)="{ item }">
                <b-badge variant="secondary">{{ item.platform_name }}</b-badge>
              </template>

              <template #cell(offer_url)="{ item }">
                <span v-if="item.offer_url" class="d-flex align-items-center">
                  <a
                    :href="item.offer_url"
                    target="_blank"
                    class="text-truncate small"
                    style="max-width: 200px; display: inline-block;"
                    :title="item.offer_url"
                  >{{ item.offer_url }}</a>
                  <b-button
                    variant="link"
                    size="sm"
                    class="p-0 ml-1"
                    @click="copyToClipboard(item.offer_url)"
                    title="Copy URL"
                  >
                    <i class="fas fa-copy"></i>
                  </b-button>
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(categories)="{ item }">
                <span v-if="getAttributesByType(item, 'category').length">
                  <b-badge
                    v-for="attr in getAttributesByType(item, 'category')"
                    :key="attr.id"
                    variant="info"
                    class="mr-1 mb-1"
                  >{{ attr.label_fr }}</b-badge>
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(experience)="{ item }">
                <span v-if="getAttributesByType(item, 'experience').length">
                  <b-badge
                    v-for="attr in getAttributesByType(item, 'experience')"
                    :key="attr.id"
                    variant="dark"
                    class="mr-1 mb-1"
                  >{{ attr.label_fr }}</b-badge>
                </span>
                <span v-else class="text-muted">-</span>
              </template>

              <template #cell(status)="{ item }">
                <b-badge :variant="statusVariant(item.status)">
                  {{ item.status }}
                </b-badge>
              </template>

              <template #cell(billed)="{ item }">
                <b-form-checkbox
                  :checked="item.billed"
                  @change="toggleBilled(item)"
                  switch
                ></b-form-checkbox>
              </template>

              <template #cell(created_at)="{ item }">
                <span class="small">{{ formatDate(item.created_at) }}</span>
              </template>

              <template #cell(published_at)="{ item }">
                <span class="small">{{ formatDate(item.published_at) }}</span>
              </template>

              <template #cell(actions)="{ item }">
                <b-button
                  v-if="item.status === 'pending' || item.status === 'unpublished'"
                  variant="success"
                  size="sm"
                  @click="updateStatus(item, 'published')"
                >
                  <i class="fas fa-check mr-1"></i> Publish
                </b-button>
                <b-button
                  v-if="item.status === 'published'"
                  variant="warning"
                  size="sm"
                  @click="updateStatus(item, 'unpublished')"
                >
                  <i class="fas fa-times mr-1"></i> Unpublish
                </b-button>
              </template>
            </BTable>
          </div>

          <!-- Pagination -->
          <BPagination
            v-if="total > perPage"
            :disabled="isBusy"
            v-model="currentPage"
            @input="fetchDiffusions"
            :total-rows="total"
            :per-page="perPage"
            align="center"
          ></BPagination>
        </div>
      </div>

      <!-- Feeds Section -->
      <div class="card mt-4">
        <div class="card-header">
          <h4 class="card-title">Platform Feeds</h4>
          <p class="card-category">XML feeds generated per diffusion platform</p>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <BTable
              :items="feeds"
              :fields="feedFields"
              :busy="isFeedsBusy"
              hover
              show-empty
              empty-text="No feeds available"
            >
              <template #table-busy>
                <div class="text-center my-4">
                  <b-spinner class="align-middle"></b-spinner>
                  <span class="ml-2">Loading...</span>
                </div>
              </template>

              <template #cell(platform_name)="{ item }">
                <b-badge variant="secondary">{{ item.platform_name }}</b-badge>
              </template>

              <template #cell(feed_url)="{ item }">
                <span class="d-flex align-items-center">
                  <a
                    :href="item.feed_url"
                    target="_blank"
                    class="text-truncate small"
                    style="max-width: 350px; display: inline-block;"
                    :title="item.feed_url"
                  >{{ item.feed_url }}</a>
                  <b-button
                    variant="link"
                    size="sm"
                    class="p-0 ml-1"
                    @click="copyToClipboard(item.feed_url)"
                    title="Copy URL"
                  >
                    <i class="fas fa-copy"></i>
                  </b-button>
                </span>
              </template>

              <template #cell(offer_count)="{ item }">
                <b-badge variant="primary">{{ item.offer_count }}</b-badge>
              </template>

              <template #cell(feed_actions)="{ item }">
                <b-button
                  variant="primary"
                  size="sm"
                  :disabled="generatingFeeds[item.platform_name]"
                  @click="generateFeed(item.platform_name)"
                >
                  <b-spinner v-if="generatingFeeds[item.platform_name]" small class="mr-1"></b-spinner>
                  <i v-else class="fas fa-sync-alt mr-1"></i>
                  Generate
                </b-button>
              </template>
            </BTable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BTable,
  BSpinner,
  BPagination,
  BBadge,
  BButton,
  BFormInput,
  BFormSelect,
  BFormCheckbox
} from "bootstrap-vue";

export default {
  components: {
    BTable,
    BSpinner,
    BPagination,
    BBadge,
    BButton,
    BFormInput,
    BFormSelect,
    BFormCheckbox
  },
  data() {
    return {
      diffusions: [],
      currentPage: 1,
      perPage: 25,
      total: 0,
      isBusy: true,
      platforms: [],
      feeds: [],
      isFeedsBusy: true,
      generatingFeeds: {},
      feedFields: [
        { key: "platform_name", label: "Platform" },
        { key: "feed_url", label: "Feed URL" },
        { key: "offer_count", label: "Published Offers" },
        { key: "feed_actions", label: "Actions" }
      ],
      filters: {
        search: "",
        status: null,
        platform: null,
        billed: null,
        ats_unpublished: false,
        removed_from_feed: false
      },
      statusOptions: [
        { value: null, text: "All statuses" },
        { value: "pending", text: "Pending" },
        { value: "published", text: "Published" },
        { value: "unpublished", text: "Unpublished" }
      ],
      billedOptions: [
        { value: null, text: "All billing" },
        { value: "true", text: "Billed" },
        { value: "false", text: "Not billed" }
      ],
      fields: [
        { key: "title", label: "Job Title" },
        { key: "company", label: "Company" },
        { key: "platform_name", label: "Platform" },
        { key: "offer_url", label: "URL" },
        { key: "categories", label: "Categories" },
        { key: "experience", label: "Experience" },
        { key: "status", label: "Status" },
        { key: "billed", label: "Billed" },
        { key: "created_at", label: "Date" },
        { key: "published_at", label: "Published" },
        { key: "actions", label: "Actions" }
      ]
    };
  },
  computed: {
    platformOptions() {
      return [
        { value: null, text: "All platforms" },
        ...this.platforms.map(p => ({ value: p, text: p }))
      ];
    }
  },
  created() {
    this.fetchDiffusions();
    this.fetchFeeds();
  },
  methods: {
    fetchDiffusions() {
      this.isBusy = true;
      this.$http
        .get("/enroll_admin/diffusions", {
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            status: this.filters.status,
            platform: this.filters.platform,
            billed: this.filters.billed,
            search: this.filters.search || undefined,
            ats_unpublished: this.filters.ats_unpublished || undefined,
            removed_from_feed: this.filters.removed_from_feed || undefined
          }
        })
        .then(({ data }) => {
          this.diffusions = data.diffusions.data;
          this.currentPage = data.diffusions.current_page;
          this.total = data.diffusions.total;
          this.platforms = data.platforms;
        })
        .finally(() => {
          this.isBusy = false;
        });
    },

    getOfferTitle(offer) {
      if (!offer || !offer.contents || offer.contents.length === 0) return "-";
      return offer.contents[0].title;
    },

    getAttributesByType(item, type) {
      if (!item.grouped_attributes || !item.grouped_attributes[type]) return [];
      return item.grouped_attributes[type];
    },

    statusVariant(status) {
      switch (status) {
        case "pending":
          return "warning";
        case "published":
          return "success";
        case "unpublished":
          return "secondary";
        default:
          return "light";
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      return new Date(dateStr).toLocaleDateString();
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
    },

    updateStatus(item, status) {
      this.$http
        .put(`/enroll_admin/diffusions/${item.id}/status`, { status })
        .then(({ data }) => {
          const index = this.diffusions.findIndex(d => d.id === item.id);
          if (index !== -1) {
            this.$set(this.diffusions, index, {
              ...this.diffusions[index],
              status: data.status,
              published_at: data.published_at,
              unpublished_at: data.unpublished_at
            });
          }
        });
    },

    fetchFeeds() {
      this.isFeedsBusy = true;
      this.$http
        .get("/enroll_admin/diffusion-feeds")
        .then(({ data }) => {
          this.feeds = data.feeds;
        })
        .finally(() => {
          this.isFeedsBusy = false;
        });
    },

    generateFeed(platformName) {
      this.$set(this.generatingFeeds, platformName, true);
      this.$http
        .post("/enroll_admin/diffusion-feeds/generate", {
          platform_name: platformName
        })
        .then(() => {
          this.$set(this.generatingFeeds, platformName, false);
        })
        .catch(() => {
          this.$set(this.generatingFeeds, platformName, false);
        });
    },

    toggleBilled(item) {
      const newBilled = !item.billed;
      this.$http
        .put(`/enroll_admin/diffusions/${item.id}/billing`, {
          billed: newBilled
        })
        .then(({ data }) => {
          const index = this.diffusions.findIndex(d => d.id === item.id);
          if (index !== -1) {
            this.$set(this.diffusions, index, {
              ...this.diffusions[index],
              billed: data.billed,
              billed_at: data.billed_at
            });
          }
        });
    }
  }
};
</script>

<style scoped>
.card-category {
  color: #9a9a9a;
  font-size: 14px;
}
</style>
